import React from "react";
import type { SingleValue } from "react-select";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import { emptyOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { ApiValue } from "../../Forms/formHelpers";
import { getDefaultValue, multipleValue } from "../../Forms/formHelpers";
import t from "../../../../../i18n";
import { uniq } from "diagnostics-lib/array";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../../../../../api/useCategories";
import type { ClassificationQuantity } from "../../../../../api/useClassificationQuantities";

// System (unique with full option)
export const useSystemInput = (selectedQuantities: ClassificationQuantity[]) => {
  const [placeholder, setPlaceholder] = React.useState(
    t("setup.paragraphs.quantities.table.definitions.column.system"),
  );
  const systems = useCategories("SYSTEM");
  const createSystem = useCreateCategory("SYSTEM");
  const updateSystem = useUpdateCategory("SYSTEM");
  const deleteSystem = useDeleteCategory("SYSTEM");
  const [dirty, setDirty] = React.useState(false);

  const systemIds = uniq(
    selectedQuantities
      .map(q => q.systemId)
      .map(c => ({
        id: c ?? "",
        name: c ?? "",
      })),
  );
  const defaultValue = getDefaultValue<ApiValue>(systemIds, "id", system => ({
    label: String(system.name),
    value: system.id,
  }));

  const [defaultSystemOption, setDefaultSystemOption] = React.useState(() => {
    if (defaultValue?.label === "" || defaultValue?.label === multipleValue.label) {
      return null;
    }
    return defaultValue;
  });

  const systemOptions = React.useMemo(
    () => [
      { id: emptyOption.value, name: emptyOption.label },
      ...systems.map(c => ({ id: c, name: c })),
    ],
    [systems],
  );

  const [selectedSystems, setSelectedSystems] =
    React.useState<SingleValue<BaseOption>>(defaultSystemOption);

  const handleSystemChanged = React.useCallback((option: SingleValue<BaseOption>) => {
    setSelectedSystems(option);
  }, []);

  React.useEffect(() => {
    if (selectedSystems?.value === defaultSystemOption?.value) {
      setDirty(false);
    } else {
      setDirty(true);
    }
  }, [selectedSystems, defaultSystemOption]);

  const resetValue = React.useCallback(() => {
    setSelectedSystems(defaultSystemOption);
    setDirty(false);
  }, [defaultSystemOption]);

  const handleSystemCreated = React.useCallback(
    async (newSystem: string) => {
      await createSystem(newSystem);
      setDirty(true);

      setSelectedSystems({
        label: newSystem,
        value: newSystem,
      });
    },
    [createSystem],
  );

  const handleSystemEdited = React.useCallback(
    async (option: BaseOption, label: string) => {
      if (!option.value) return;
      await updateSystem(option.value, label);
      if (selectedSystems?.value === option.value) {
        setSelectedSystems({ ...option, label });
      }
    },
    [updateSystem, setSelectedSystems, selectedSystems],
  );

  React.useEffect(() => {
    if (defaultValue?.value === multipleValue.value)
      setPlaceholder(t("administration.modal.multiple_value"));
    else setPlaceholder(t("form.no_value"));
  }, [defaultValue]);

  const handleSystemDeleted = React.useCallback(
    async (option: BaseOption) => {
      if (!option.value) return;
      await deleteSystem(option.value);
      if (selectedSystems?.value === option.value) {
        setDefaultSystemOption(null);
        setSelectedSystems(null);
      }
    },
    [deleteSystem, selectedSystems],
  );

  return {
    placeholder,
    systemOptions,
    selectedSystems,
    setSelectedSystems,
    handleSystemChanged,
    handleSystemCreated,
    handleSystemEdited,
    handleSystemDeleted,
    dirty,
    resetValue,
  };
};
